import { audio } from '../../../../../audio/AudioManager.js';
import { SaveManager } from '../../../../../state/SaveManager.js';
import { applicationLifecycle } from '../../../../shared/lifecycle/index.js';
import { NUMBER_KINGDOM_ASSETS } from '../../NumberKingdomAssets.js';
import {
  NUMBER_KINGDOM_DISTRICTS,
  completeNumberKingdomDistrict,
  getNumberKingdomState,
  visitNumberKingdomDistrict,
} from '../../NumberKingdomState.js';
import { ArrivalInputController } from '../../components/ArrivalInputController.js';
import {
  createNumberKingdomHomeButton,
  type NumberKingdomHomeButton,
} from '../../components/NumberKingdomHomeButton.js';
import {
  configureNumberKingdomDistrictRenderer,
  getNumberKingdomDistrictLayout,
} from '../../components/NumberKingdomDistrictLayout.js';
import { ADDITION_CITADEL_ASSETS } from '../addition-citadel/AdditionCitadelAssets.js';
import { discoverAdditionCitadel } from '../addition-citadel/AdditionCitadelState.js';
import { ADDITION_CITADEL_SCENE_KEY } from '../addition-citadel/AdditionCitadelScene.js';

export const KINGDOM_CROSSROADS_SCENE_KEY = 'KingdomCrossroads';

const FONT_UI = '"Nunito", "Segoe UI", Arial, sans-serif';

export class KingdomCrossroadsScene extends Phaser.Scene {
  private background!: Phaser.GameObjects.Image;
  private cosmo!: Phaser.GameObjects.Image;
  private destinationLabel!: Phaser.GameObjects.Text;
  private controlsHint!: Phaser.GameObjects.Text;
  private backLabel!: Phaser.GameObjects.Text;
  private homeButton!: NumberKingdomHomeButton;
  private roadZone!: Phaser.GameObjects.Zone;
  private inputController?: ArrivalInputController;
  private unsubscribeLifecycle?: () => void;
  private resizeTimer?: number;
  private transitioning = false;

  constructor() {
    super({ key: KINGDOM_CROSSROADS_SCENE_KEY });
  }

  create() {
    if (!getNumberKingdomState().bridgeCrossingUnlocked) {
      this.scene.start('NumberKingdom');
      return;
    }

    this.transitioning = false;
    configureNumberKingdomDistrictRenderer(this);
    void audio.enterSoundscape('number-kingdom');
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads);

    this.background = this.add.image(
      640,
      360,
      ADDITION_CITADEL_ASSETS.kingdomCrossroads.key,
    ).setDepth(0).setCrop(72, 0, 1136, 700).setScale(1280 / 1136, 720 / 700);

    this.cosmo = this.add.image(
      310,
      590,
      NUMBER_KINGDOM_ASSETS.numberKingdomCosmo.key,
    ).setDepth(20).setDisplaySize(170, 152);

    this.destinationLabel = this.add.text(930, 170, 'ADDITION CITADEL\nFollow the golden road', {
      fontFamily: FONT_UI,
      fontSize: '28px',
      fontStyle: 'bold',
      align: 'center',
      color: '#fff4bd',
      stroke: '#3d175f',
      strokeThickness: 7,
      shadow: { offsetX: 0, offsetY: 3, blur: 6, color: '#1d0838', fill: true },
    }).setOrigin(0.5).setDepth(30);

    this.controlsHint = this.add.text(1260, 688,
      'Walk: A/D or ←/→  •  Touch the road  •  Enter: travel', {
        fontFamily: FONT_UI,
        fontSize: '16px',
        fontStyle: 'bold',
        color: '#fff8d8',
        stroke: '#2d1245',
        strokeThickness: 5,
      }).setOrigin(1, 1).setDepth(40);

    this.backLabel = this.add.text(20, 20, '←  Back across Star Bridge', {
      fontFamily: FONT_UI,
      fontSize: '18px',
      fontStyle: 'bold',
      color: '#ffffff',
      stroke: '#2d1245',
      strokeThickness: 6,
    }).setDepth(40).setInteractive({ useHandCursor: true });
    this.backLabel.on('pointerdown', this.returnToStarBridge, this);
    this.homeButton = createNumberKingdomHomeButton(this, this.returnHome);

    this.roadZone = this.add.zone(1000, 430, 360, 420)
      .setDepth(25)
      .setInteractive({ useHandCursor: true });
    this.roadZone.on('pointerdown', this.travelToAdditionCitadel, this);

    this.inputController = new ArrivalInputController(this, this.cosmo, {
      minX: 250,
      maxX: 1030,
      baselineY: 590,
      speed: 230,
      depth: 22,
      onPositionChanged: x => {
        if (x >= 920) this.travelToAdditionCitadel();
      },
    });
    this.inputController.enable();

    this.input.keyboard?.on('keydown-ENTER', this.travelToAdditionCitadel, this);
    this.input.keyboard?.on('keydown-SPACE', this.travelToAdditionCitadel, this);
    this.input.keyboard?.on('keydown-ESC', this.returnToStarBridge, this);
    this.input.keyboard?.on('keydown-M', this.returnHome, this);
    window.addEventListener('resize', this.handleResize);
    this.unsubscribeLifecycle = applicationLifecycle.subscribe({
      onSuspend: () => this.inputController?.disable(),
      onResume: () => {
        if (!this.transitioning) this.inputController?.enable();
      },
    });
    this.layout();

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, this.shutdown, this);
  }

  private readonly travelToAdditionCitadel = () => {
    if (this.transitioning) return;
    this.transitioning = true;
    this.inputController?.disable();
    audio.unlock();
    completeNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.kingdomCrossroads);
    discoverAdditionCitadel();
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.additionCitadel);
    this.cameras.main.fadeOut(280, 255, 211, 102);
    this.time.delayedCall(300, () => this.scene.start(ADDITION_CITADEL_SCENE_KEY));
  };

  private readonly returnToStarBridge = () => {
    if (this.transitioning) return;
    this.transitioning = true;
    this.inputController?.disable();
    visitNumberKingdomDistrict(NUMBER_KINGDOM_DISTRICTS.starBridge);
    this.scene.start('NumberKingdom');
  };

  private readonly returnHome = () => {
    if (this.transitioning) return;
    this.transitioning = true;
    this.inputController?.disable();
    SaveManager.setLastScene(KINGDOM_CROSSROADS_SCENE_KEY);
    SaveManager.save();
    this.scene.start('HomeHub');
  };

  private readonly handleResize = () => {
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      if (!this.scene.isActive()) return;
      configureNumberKingdomDistrictRenderer(this);
      this.layout();
    }, 80);
  };

  private layout() {
    const { safe, compact } = getNumberKingdomDistrictLayout(this);
    this.backLabel.setPosition(safe.x + (compact ? 182 : 206), safe.y + 12);
    this.homeButton.layout(safe, compact, 'left');
    this.controlsHint.setPosition(safe.right, safe.bottom);
    this.destinationLabel.setPosition(
      compact ? safe.right - 190 : safe.right - 240,
      safe.y + (compact ? 125 : 150),
    ).setFontSize(compact ? 23 : 28);
  }

  private shutdown() {
    SaveManager.save();
    this.unsubscribeLifecycle?.();
    this.unsubscribeLifecycle = undefined;
    audio.leaveSoundscape({ id: 'number-kingdom' });
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeTimer !== undefined) window.clearTimeout(this.resizeTimer);
    this.input.keyboard?.off('keydown-ENTER', this.travelToAdditionCitadel, this);
    this.input.keyboard?.off('keydown-SPACE', this.travelToAdditionCitadel, this);
    this.input.keyboard?.off('keydown-ESC', this.returnToStarBridge, this);
    this.input.keyboard?.off('keydown-M', this.returnHome, this);
  }
}
